import React, { useState } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import {
  Box,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { ListFilter, UserRoundX, UsersRound } from "lucide-react";
import * as Yup from "yup";

import Filters from "../../components/Filter";
import {
  UIAccordion,
  UIActionModal,
  UIAvatar,
  UIAvatarStack,
  UIButton,
  UIBulkActions,
  UICallout,
  UIAlert,
  UIBarChart,
  UIChart,
  UIChangeSummary,
  UIChip,
  UICheckbox,
  UIDatePicker,
  UIDateRangeFields,
  UIEmptyState,
  FilePreviewModal,
  UIInput,
  UIIcon,
  UIModal,
  UINumberField,
  UIPaginationBar,
  UIPageHeader,
  UIPasswordField,
  UIPhoneField,
  UIProgressBar,
  UIRadioGroup,
  UISearchField,
  UISelect,
  UIStatusTag,
  UITable,
  TabSetsLevels,
  UITabs,
  UITextField,
  UITextarea,
  UITimeField,
  UIToggle,
  UIUpload,
  UIUploadedFile,
  UIUploadProgress,
  UIUserInfo,
} from "../../components/ui";
import { uiTokens } from "../../utils/Theme";
import { createCssVariables } from "../../utils/uiTokens";
import "./UIKit.scss";

const flattenTokens = (tokens, prefix = "") =>
  Object.entries(tokens).flatMap(([name, value]) => {
    const key = prefix ? `${prefix}/${name}` : name;
    return typeof value === "string" ? [[key, value]] : flattenTokens(value, key);
  });

const colorGroups = [
  ["color/primitive/neutral", flattenTokens(uiTokens.color.primitive.neutral)],
  ["color/primitive/primary", flattenTokens(uiTokens.color.primitive.primary)],
  ["color/primitive/success", flattenTokens(uiTokens.color.primitive.success)],
  ["color/primitive/warning", flattenTokens(uiTokens.color.primitive.warning)],
  ["color/primitive/error", flattenTokens(uiTokens.color.primitive.error)],
  ["color/primitive/fuchsia", flattenTokens(uiTokens.color.primitive.fuchsia)],
  ["color/primitive/cyan", flattenTokens(uiTokens.color.primitive.cyan)],
  ["color/semantic", flattenTokens(uiTokens.color.semantic)],
  ["color/app", flattenTokens(uiTokens.color.app)],
];

const utilityColorTokens = Object.keys(createCssVariables())
  .filter((name) => name.startsWith("--color-") && !name.endsWith("-rgb"))
  .map((name) => name.replace("--color-", ""))
  .sort();

const spacingUtilityPrefixes = [
  "m",
  "p",
  "mt",
  "mr",
  "mb",
  "ml",
  "mx",
  "my",
  "ms",
  "me",
  "pt",
  "pr",
  "pb",
  "pl",
  "px",
  "py",
  "ps",
  "pe",
  "mbs",
  "mbe",
  "pbs",
  "pbe",
];

const tokenUtilityGroups = [
  {
    title: "Text Colors",
    classes: utilityColorTokens.map((token) => `text-${token}`),
  },
  {
    title: "Background Colors",
    classes: utilityColorTokens.map((token) => `bg-${token}`),
  },
  {
    title: "Border Colors",
    classes: utilityColorTokens.map((token) => `border-${token}`),
  },
  {
    title: "SVG Colors",
    classes: utilityColorTokens.flatMap((token) => [
      `fill-${token}`,
      `stroke-${token}`,
    ]),
  },
  {
    title: "Typography",
    classes: Object.entries(uiTokens.typography).flatMap(([group, sizes]) =>
      Object.keys(sizes).map((size) => `text-${group}-${size}`)
    ),
  },
  {
    title: "Font Weight",
    classes: Object.values(uiTokens.fontWeight).map((weight) => `font-${weight}`),
  },
  {
    title: "Spacing",
    classes: Object.keys(uiTokens.space).flatMap((size) =>
      spacingUtilityPrefixes.map((prefix) => `${prefix}-${size}`)
    ),
  },
  {
    title: "Rounded",
    classes: Object.keys(uiTokens.radius).map((size) => `rounded-${size}`),
  },
  {
    title: "Shadows",
    classes: Object.keys(uiTokens.shadow).map((size) => `shadow-${size}`),
  },
];

const buttonSizes = ["small", "medium", "large"];

const buttonDemoRows = [
  {
    title: "Primary",
    props: { variant: "primary" },
    hasOutline: true,
  },
  {
    title: "Secondary",
    props: { variant: "secondary" },
    hasOutline: true,
  },
  {
    title: "Outline",
    props: { variant: "outline" },
  },
  {
    title: "Ghost",
    props: { variant: "ghost" },
    hasOutline: true,
  },
  {
    title: "Destructive",
    props: { variant: "destructive" },
    hasOutline: true,
  },
  {
    title: "Link",
    props: { variant: "link" },
  },
  {
    title: "Success",
    props: { variant: "success" },
    hasOutline: true,
  },
];

const tableStatusTags = ["Pending", "Upcoming", "Ongoing", "Completed", "Canceled"];
const headerStatusTags = ["Ongoing", "Upcoming", "Draft", "Verified", "Open", "Closed"];
const avatarDemoImage =
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=160&q=80";

const jsxUtilityExample = `<div className="bg-primary-0 border-primary-100 rounded-xl p-24 shadow-sm">
  <h3 className="text-heading-md text-primary-900 mb-8">Shift details</h3>
  <p className="text-body-sm text-foreground-muted m-0">
    Reusable token utilities keep component SCSS small.
  </p>
</div>`;

const scssUtilityExample = `.shift-card {
  @include ui-card-surface;
  padding: var(--space-24);
}

.shift-card__title {
  @include ui-typography(heading, md);
  color: var(--color-primary-900);
}`;

const selectOptions = [
  { id: 1, name: "Approved" },
  { id: 2, name: "Pending" },
  { id: 3, name: "Deactivated" },
];

const roleOptions = [
  { label: "Full Time", value: "full-time" },
  { label: "Part Time", value: "part-time" },
  { label: "Freelance", value: "freelance" },
];

const sidebarIcons = [
  "dashboard",
  "company",
  "events",
  "requests",
  "users",
  "userPlus",
  "revenue",
  "settings",
  "logout",
  "clients",
];

const utilityIcons = ["settings1", "registration", "demo", "list"];

/** Commonly registered glyphs (roles, clocks, IDs) beyond sidebar + utility. */
const miscellaneousIcons = [
  "idUser",
  "twoUsers",
  "user",
  "clock",
  "locationPin",
];

const EVENT_VIEW_ROLE_SAMPLES = [
  {
    caption: "Supervisor (event view → Roles)",
    tileSx: {
      width: 48,
      height: 48,
      borderRadius: "var(--rounded-xl)",
      backgroundColor: "var(--color-info-subtle)",
      alignItems: "center",
      display: "flex",
      justifyContent: "center",
    },
    icon: (
      <UIIcon
        name="idUser"
        width={26}
        height={26}
        color="var(--color-primary-900)"
        decorative
      />
    ),
    tokenNote: "--color-info-subtle + --color-primary-900",
  },
  {
    caption: "Other roles (event view → Roles)",
    tileSx: {
      width: 48,
      height: 48,
      borderRadius: "var(--rounded-xl)",
      backgroundColor: "var(--color-primary-0)",
      alignItems: "center",
      display: "flex",
      justifyContent: "center",
    },
    icon: (
      <UIIcon
        name="twoUsers"
        width={28}
        height={28}
        color="var(--color-primary-500)"
        decorative
      />
    ),
    tokenNote: "--color-primary-0 + --color-primary-500",
  },
];

const tableColumns = [
  {
    field: "code",
    headerName: "ID",
    width: 100,
  },
  { field: "company", headerName: "Company", flex: 1, minWidth: 180 },
  { field: "city", headerName: "City", width: 140 },
  { field: "events", headerName: "Events", width: 110 },
  {
    field: "status",
    headerName: "Status",
    width: 140,
    renderCell: (params) => (
      <Chip
        label={params.value}
        color={params.value === "Active" ? "success" : "default"}
        size="small"
      />
    ),
  },
];

const tableRows = [
  { id: 1, code: "#TA135", company: "Al-Riyadh Events Co.", city: "Riyadh", events: 24, status: "Active" },
  { id: 2, code: "#TA136", company: "Jeddah Celebrations Ltd.", city: "Jeddah", events: 12, status: "Active" },
  { id: 3, code: "#TA137", company: "Elite Gatherings", city: "Mecca", events: 0, status: "Paused" },
];

const sampleText = "The quick brown fox jumps over the lazy dog.";
const fontWeightLabel = {
  400: "Regular",
  500: "Medium",
  600: "SemiBold",
  700: "Bold",
};

const pxFromRem = (value) => {
  if (value === "0rem") return 0;
  if (value.endsWith("rem")) return Number.parseFloat(value) * 16;
  if (value.endsWith("px")) return Number.parseFloat(value);
  return value;
};

const typographyRows = Object.entries(uiTokens.typography).flatMap(
  ([groupName, group]) =>
    Object.entries(group).map(([sizeName, style]) => ({
      id: `${groupName}-${sizeName}`,
      name: `${groupName}/${sizeName}`,
      style,
    }))
);

const spacingRows = Object.entries(uiTokens.space).map(([name, value]) => ({
  id: name,
  name,
  value,
  px: pxFromRem(value),
}));

const breakpointRows = Object.entries(uiTokens.breakpoint).map(
  ([name, value]) => ({
    id: name,
    prefix: name,
    minWidth: value,
    css: `@media (min-width: ${value})`,
  })
);

const formInitialValues = {
  companyName: "",
  search: "",
  password: "",
  status: null,
  phone: "",
  launchDate: "",
  shiftTime: "",
  budget: "",
  roleType: "full-time",
  termsAccepted: false,
  autoApprove: true,
  notes: "",
};

const formValidationSchema = Yup.object({
  companyName: Yup.string().required("Company name is required."),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters.")
    .required("Password is required."),
  status: Yup.object().nullable().required("Status is required."),
  phone: Yup.string().min(8, "Phone number is invalid.").required("Phone number is required."),
  launchDate: Yup.string().required("Date is required."),
  shiftTime: Yup.string().required("Time is required."),
  budget: Yup.number()
    .typeError("Budget must be a number.")
    .min(0, "Budget must be 0 or more.")
    .required("Budget is required."),
  roleType: Yup.string().required("Role type is required."),
  termsAccepted: Yup.boolean().oneOf([true], "You must accept the terms."),
  notes: Yup.string()
    .min(20, "Description should be at least 20 characters.")
    .required("Description is required."),
});

const sectionIdFromTitle = (title) =>
  String(title).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const UIKitSection = ({ title, subtitle, action, children, defaultExpanded }) => (
  <UIAccordion
    id={`ui-kit-${sectionIdFromTitle(title)}`}
    defaultExpanded={defaultExpanded}
    hasCustomizeTitle
    title={
      <Box>
        <Typography className="ui-kit-section__title">{title}</Typography>
        {subtitle && (
          <Typography className="ui-kit-section__subtitle">{subtitle}</Typography>
        )}
      </Box>
    }
    action={action}
    classNames={{
      root: "ui-kit-section",
      summary: "ui-kit-section__summary",
      body: "ui-kit-section__body",
    }}
  >
    {children}
  </UIAccordion>
);

UIKitSection.defaultProps = {
  action: null,
  defaultExpanded: false,
  subtitle: "",
};

UIKitSection.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  action: PropTypes.node,
  children: PropTypes.node.isRequired,
  defaultExpanded: PropTypes.bool,
};

const UIKit = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("info");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [tab, setTab] = useState("overview");
  const [statusTab, setStatusTab] = useState("pending");
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [logoUploadPreview, setLogoUploadPreview] = useState("");
  const [logoUploadFile, setLogoUploadFile] = useState(null);
  const [documentUploadPreview, setDocumentUploadPreview] = useState("");
  const [documentUploadFile, setDocumentUploadFile] = useState(null);
  const [paginationPage, setPaginationPage] = useState(1);
  const [paginationPageSize, setPaginationPageSize] = useState(10);
  const [rangeDemoStart, setRangeDemoStart] = useState("");
  const [rangeDemoEnd, setRangeDemoEnd] = useState("");
  const [searchFilterQuery, setSearchFilterQuery] = useState("");
  const [searchFilterValues, setSearchFilterValues] = useState({
    status: null,
    role: null,
    city: null,
    company: null,
  });
  const updateSearchFilter = (name) => (value) => {
    setSearchFilterValues((current) => ({ ...current, [name]: value }));
  };

  return (
    <main className="ui-kit-page">
      <Box className="ui-kit-page__hero">
        <Typography variant="overline">Yoram UI Foundation</Typography>
        <Typography variant="h3" component="h1">
          Design System Preview
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 760 }}>
          Internal reference for the new MUI-based components, color tokens,
          spacing, radius, and states. This route is intentionally hidden from
          production navigation.
        </Typography>
      </Box>

      <Stack spacing={3}>
        <UIKitSection title="Colors" subtitle="CSS variables and MUI theme tokens">
          <Grid container spacing={2}>
            {colorGroups.map(([groupName, colors]) => (
              <Grid item xs={12} md={6} lg={4} key={groupName}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {groupName}
                </Typography>
                <Stack spacing={1}>
                  {colors.map(([name, value]) => (
                    <Box className="ui-kit-token-row" key={`${groupName}-${name}`}>
                      <span
                        className="ui-kit-token-row__swatch"
                        style={{ backgroundColor: value }}
                      />
                      <span>{name}</span>
                      <code>{value}</code>
                    </Box>
                  ))}
                </Stack>
              </Grid>
            ))}
          </Grid>
        </UIKitSection>

        <UIKitSection title="Token Utilities" subtitle="classes generated from UI tokens">
          <Stack spacing={3}>
            <Grid container rowSpacing={3} columnSpacing={2}>
              {tokenUtilityGroups.map((group) => (
                <Grid item xs={12} md={6} key={group.title}>
                  <Box className="ui-kit-utility-group">
                    <Typography className="ui-kit-utility-group__title">
                      {group.title} ({group.classes.length})
                    </Typography>
                    <Stack
                      className="ui-kit-utility-group__list"
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {group.classes.map((className) => (
                        <code className="ui-kit-utility-chip" key={className}>
                          {className}
                        </code>
                      ))}
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <Box className="bg-primary-0 border-primary-100 rounded-xl p-24 shadow-sm">
                  <Typography className="text-heading-md text-primary-900 mb-8">
                    Shift details
                  </Typography>
                  <Typography className="text-body-sm text-foreground-muted m-0">
                    Reusable token utilities keep component SCSS small.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={7}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      JSX
                    </Typography>
                    <pre className="ui-kit-code-block">
                      <code>{jsxUtilityExample}</code>
                    </pre>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      SCSS
                    </Typography>
                    <pre className="ui-kit-code-block">
                      <code>{scssUtilityExample}</code>
                    </pre>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Stack>
        </UIKitSection>

        <UIKitSection title="Icons" subtitle="Reusable SVG icons rendered through UIIcon">
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Sidebar
              </Typography>
              <Grid container spacing={2}>
                {sidebarIcons.map((name) => (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={name}>
                    <Box className="ui-kit-icon-sample">
                      <Box className="ui-kit-icon-sample__preview ui-kit-icon-sample__preview--dark">
                        <UIIcon name={name} />
                        <UIIcon name={name} selected />
                      </Box>
                      <code>{name}</code>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Utility
              </Typography>
              <Grid container spacing={2}>
                {utilityIcons.map((name) => (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={name}>
                    <Box className="ui-kit-icon-sample">
                      <Box className="ui-kit-icon-sample__preview ui-kit-icon-sample__preview--dark">
                        <UIIcon name={name} />
                        <UIIcon name={name} selected />
                      </Box>
                      <code>{name}</code>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Miscellaneous (events &amp; IDs)
              </Typography>
              <Grid container spacing={2}>
                {miscellaneousIcons.map((name) => (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={name}>
                    <Box className="ui-kit-icon-sample">
                      <Box className="ui-kit-icon-sample__preview ui-kit-icon-sample__preview--dark">
                        <UIIcon name={name} />
                      </Box>
                      <code>{name}</code>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Event view — Roles row (48×48 tile)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Tokens: <code>{EVENT_VIEW_ROLE_SAMPLES[0].tokenNote}</code>;{" "}
                <code>{EVENT_VIEW_ROLE_SAMPLES[1].tokenNote}</code>
              </Typography>
              <Grid container spacing={3}>
                {EVENT_VIEW_ROLE_SAMPLES.map((sample) => (
                  <Grid item xs={12} sm={6} key={sample.caption}>
                    <Stack spacing={1}>
                      <Typography variant="caption">{sample.caption}</Typography>
                      <Box sx={sample.tileSx}>{sample.icon}</Box>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Stack>
        </UIKitSection>

        <UIKitSection title="Typography Scale" subtitle="Outfit styles from Figma">
          <Stack>
            {typographyRows.map(({ id, name, style }) => (
              <Box className="ui-kit-type-row" key={id}>
                <Box className="ui-kit-type-row__meta">
                  <strong>{name}</strong>
                  <span>
                    Outfit {fontWeightLabel[style.fontWeight] || style.fontWeight}
                  </span>
                  <span>
                    {pxFromRem(style.fontSize)}px / {pxFromRem(style.lineHeight)}px
                  </span>
                </Box>
                <Typography sx={style}>{sampleText}</Typography>
              </Box>
            ))}
          </Stack>
        </UIKitSection>

        <UIKitSection title="Spacing Scale" subtitle="Figma spacing primitives">
          <Stack spacing={1.5}>
            {spacingRows.map((space) => (
              <Box className="ui-kit-space-row" key={space.id}>
                <span>{space.name}</span>
                <span style={{ width: space.value }} />
                <code>{space.value}</code>
              </Box>
            ))}
          </Stack>
        </UIKitSection>

        <UIKitSection title="Border Radius" subtitle="Figma radius primitives">
          <Grid container spacing={2}>
            {Object.entries(uiTokens.radius).map(([name, value]) => (
              <Grid item xs={12} sm={6} md={3} key={name}>
                <Box
                  className="ui-kit-radius-sample"
                  sx={{ borderRadius: value }}
                >
                  {name}: {value}
                </Box>
              </Grid>
            ))}
          </Grid>
        </UIKitSection>

        <UIKitSection title="Breakpoints" subtitle="Shared responsive prefixes">
          <UITable
            rows={breakpointRows}
            height={360}
            hideFooter
            columns={[
              { field: "prefix", headerName: "Prefix", width: 140 },
              { field: "minWidth", headerName: "Minimum width", width: 180 },
              { field: "css", headerName: "CSS", flex: 1, minWidth: 260 },
            ]}
          />
        </UIKitSection>

        <UIKitSection title="Tags" subtitle="Base chips and reusable status tags">
          <Stack spacing={3}>
            <Box>
              <Typography
                className="ui-kit-tag-row__title"
                sx={{ mb: 1.5 }}
              >
                Base Chips
              </Typography>
              <Stack spacing={1.5}>
                {["light", "solid", "ghost"].map((chipAppearance) => (
                  <Box className="ui-kit-tag-row" key={chipAppearance}>
                    <Typography sx={{ textTransform: "capitalize" }}>
                      {chipAppearance}
                    </Typography>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                      {["primary", "primarySubtle", "secondary", "success", "warning", "error", "info", "muted"].map(
                        (chipColor) => (
                          <UIChip
                            color={chipColor}
                            appearance={chipAppearance}
                            key={`${chipAppearance}-${chipColor}`}
                            label={chipColor}
                            size="small"
                          />
                        )
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
            <Box className="ui-kit-tag-row">
              <Typography className="ui-kit-tag-row__title">Table Tags</Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                {tableStatusTags.map((status) => (
                  <UIStatusTag
                    key={status}
                    label={status}
                    preset="table"
                    status={status}
                  />
                ))}
              </Stack>
            </Box>
            <Box className="ui-kit-tag-row">
              <Typography className="ui-kit-tag-row__title">Header Tags</Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                {headerStatusTags.map((status) => (
                  <UIStatusTag
                    key={status}
                    label={status}
                    preset="header"
                    status={status}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </UIKitSection>

        <UIKitSection title="Avatar" subtitle="User initials and image avatar sizes">
          <Stack
            alignItems="center"
            className="ui-kit-avatar-row"
            direction="row"
            flexWrap="wrap"
            useFlexGap
          >
            <UIAvatar name="Small Member" size="sm" />
            <UIAvatar name="Medium Designer" size="md" />
            <UIAvatar initials="L" size="lg" />
            <UIAvatar image={avatarDemoImage} name="Small Member" size="sm" />
            <UIAvatar image={avatarDemoImage} name="Medium Designer" size="md" />
            <UIAvatar image={avatarDemoImage} name="Large Manager" size="lg" />
          </Stack>
        </UIKitSection>

        <UIKitSection
          title="Chart"
          subtitle="Dependency-free donut — segment colour matches its legend entry"
        >
          <Stack alignItems="center" direction="row" flexWrap="wrap" spacing={4} useFlexGap>
            <UIChart
              ariaLabel="Events by status"
              data={[
                { id: "completed", label: "Completed", value: 14, color: "var(--color-primary-300)" },
                { id: "upcoming", label: "Upcoming", value: 9, color: "var(--color-fuchsia-500)" },
                { id: "ongoing", label: "Ongoing", value: 18, color: "var(--color-primary-default)" },
                { id: "pending", label: "Pending", value: 6, color: "var(--color-warning-hover)" },
                { id: "cancelled", label: "Cancelled", value: 3, color: "var(--color-destructive-default)" },
              ]}
            />
            <UIChart
              ariaLabel="Completion"
              size={140}
              data={[
                { id: "done", label: "Done", value: 72, color: "var(--color-primary-500)" },
                { id: "left", label: "Remaining", value: 28, color: "var(--color-primary-100)" },
              ]}
            >
              <strong className="text-heading-md">72%</strong>
            </UIChart>
          </Stack>
        </UIKitSection>

        <UIKitSection
          title="Bar Chart"
          subtitle="Dependency-free vertical bars — value label above, per-bar colour"
        >
          <Stack spacing={4}>
            <UIBarChart
              ariaLabel="Applicant engagement"
              data={[
                { id: "a", label: "Yoram", value: 18, valueLabel: "18%", color: "var(--color-primary-100, #DDD6FE)" },
                { id: "b", label: "4 Direction", value: 24, valueLabel: "24%" },
                { id: "c", label: "Trz", value: 27, valueLabel: "27%" },
                { id: "d", label: "Elriyadh", value: 16, valueLabel: "16%" },
                { id: "e", label: "Tesla", value: 12, valueLabel: "12%" },
                { id: "f", label: "Vista", value: 9, valueLabel: "9%", color: "var(--color-primary-900, #31237D)" },
              ]}
            />
            <UIBarChart
              ariaLabel="Weekly signups"
              height={120}
              data={[
                { id: "mon", label: "Mon", value: 8, valueLabel: "8" },
                { id: "tue", label: "Tue", value: 14, valueLabel: "14" },
                { id: "wed", label: "Wed", value: 5, valueLabel: "5" },
              ]}
            />
          </Stack>
        </UIKitSection>

        <UIKitSection
          title="Avatar Stack"
          subtitle="Overlapping avatars — hover for a name, click for the full list"
        >
          <Stack alignItems="center" direction="row" flexWrap="wrap" spacing={4} useFlexGap>
            <UIAvatarStack
              items={[
                { id: 1, name: "Omar Tarek" },
                { id: 2, name: "Sara Nabil" },
                { id: 3, name: "Yara Long Name" },
                { id: 4, name: "Khaled Fahad" },
                { id: 5, name: "Nour Hassan" },
              ]}
            />
            <UIAvatarStack items={[{ id: 1, name: "Omar Tarek" }]} />
            <UIAvatarStack items={[]} />
          </Stack>
        </UIKitSection>

        <UIKitSection title="Page Header" subtitle="Shared company, event, report, and profile header">
          <Stack spacing={2}>
            <UIPageHeader
              actions={
                <UIButton appearance="gradient" size="medium" startIcon={<AddIcon />}>
                  Edit Event
                </UIButton>
              }
              avatar={{ image: avatarDemoImage, name: "Yoram Event" }}
              status="ongoing"
              subtitle={["test", "Event ID: #6923"]}
              title="event type test"
            />
            <UIPageHeader
              actions={
                <UIButton appearance="gradient" size="medium" startIcon={<AddIcon />}>
                  Edit Profile
                </UIButton>
              }
              avatar={{ image: avatarDemoImage, name: "Al-Riyadh Events Co." }}
              status="active"
              subtitle={["شركة الرياض للفعاليات", "Joined 15 Jan 2024"]}
              title="Al-Riyadh Events Co."
            />
            <UIPageHeader
              icon={<ShieldOutlinedIcon />}
              status="closed"
              subtitle={["Report ID: #REP-2025-0941", "Priority: High"]}
              title="عدم وجود علامات تحذيرية"
            />
            <UIPageHeader
              actions={
                <>
                  <UIButton color="inherit" variant="outlined">
                    Escalate Report
                  </UIButton>
                  <UIButton variant="success">Resolve issue</UIButton>
                </>
              }
              icon={<ErrorOutlineIcon />}
              iconStatus="open"
              status="open"
              subtitle={["Report ID: #REP-2025-0941", "Priority: High"]}
              title="عدم وجود علامات تحذيرية"
            />
            <UIPageHeader
              actions={
                <>
                  <UIButton aria-label="Download" color="inherit" variant="outlined">
                    <DownloadIcon fontSize="small" />
                  </UIButton>
                  <UIButton color="inherit" variant="outlined">Edit Profile</UIButton>
                  <UIButton color="inherit" variant="outlined">Pending Requests</UIButton>
                  <UIButton variant="destructive">Block Applicant</UIButton>
                </>
              }
              avatar={{ image: avatarDemoImage, name: "سليم محمد بلدمير", size: "xl" }}
              status="verified"
              subtitle={["Id #Apl-004", "Role: Usher", "5.00 Excellent"]}
              title="سليم محمد بلدمير"
            >
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                <span>5 Events</span>
                <span>0h 00m Working Hours</span>
                <span>100% Attendance</span>
              </Stack>
            </UIPageHeader>
          </Stack>
        </UIKitSection>

        <UIKitSection title="Buttons" subtitle="UIButton variants, sizes, states, icons, and loading">
          <Stack spacing={3}>
            {buttonDemoRows.map(({ title, props, hasOutline }) => (
              <Box className="ui-kit-button-row" key={title}>
                <Typography className="ui-kit-button-row__title">{title}</Typography>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                  {buttonSizes.map((buttonSize) => (
                    <UIButton key={buttonSize} size={buttonSize} {...props}>
                      {buttonSize.toUpperCase()}
                    </UIButton>
                  ))}
                  {hasOutline && (
                    <UIButton appearance="outline" {...props}>
                      Outline
                    </UIButton>
                  )}
                  <UIButton startIcon={<AddIcon />} {...props}>
                    Icon
                  </UIButton>
                  <UIButton loading {...props} />
                  <UIButton disabled {...props}>
                    Disabled
                  </UIButton>
                </Stack>
              </Box>
            ))}
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <UIButton appearance="gradient" startIcon={<AddIcon />}>
                Legacy Gradient
              </UIButton>
              <UIButton variant="outlined">Legacy Outlined</UIButton>
              <UIButton variant="text">Legacy Text</UIButton>
              <UIButton color="error" startIcon={<DeleteOutlineIcon />}>
                Legacy Error
              </UIButton>
            </Stack>
          </Stack>
        </UIKitSection>

        <UIKitSection title="Upload" subtitle="Reusable image and file upload surfaces">
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <UIUpload
              label="Logo"
              variant="logo"
              uploadText="Upload Logo"
              preview={logoUploadPreview}
              file={logoUploadFile}
              onChange={(file, preview) => {
                setLogoUploadFile(file);
                setLogoUploadPreview(preview || "");
              }}
            />
            <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
              <UIUpload
                label="Document"
                uploadText="Click to upload"
                dragHint="or drag and drop"
                hint="PNG, JPG or PDF — max 10MB (one file)."
                showRemove
                preview={documentUploadPreview}
                file={documentUploadFile}
                uploadCompleted={Boolean(documentUploadPreview && !documentUploadFile)}
                successLabel="Uploaded Successfully"
                onChange={(file, preview) => {
                  setDocumentUploadFile(file);
                  setDocumentUploadPreview(preview || "");
                }}
              />
            </Box>
            <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
              <UIUploadProgress
                fileName="ID_Document.pdf"
                isPdf
                progress={65}
              />
            </Box>
            <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
              <UIUploadedFile
                fileName="Delegation_Document_2026.pdf"
                onRemove={() => {}}
              />
            </Box>
          </Stack>
        </UIKitSection>

        <UIKitSection title="Alerts" subtitle="Status feedback messages">
          <Stack spacing={2}>
            <UIAlert
              severity="success"
              title="Success"
              subTitle="Please correct the highlighted errors to proceed."
            />
            <UIAlert
              severity="failed"
              title="Submission failed"
              subTitle="Please correct the highlighted errors to proceed."
            />
          </Stack>
        </UIKitSection>

        <UIKitSection title="Callouts" subtitle="Inline contextual alerts with actions">
          <Stack spacing={2}>
            <UICallout
              variant="warning"
              title="2 supervisors: No action"
              description={
                <>
                  Use the <span className="ui-callout__emphasis">Zones</span>{" "}
                  tab to allocate them
                </>
              }
              actionLabel="Zones tab"
              onAction={() => console.log("Open zones tab")}
            />
            <UICallout
              variant="primary"
              title="Zone 3 - VIP Lounge"
              description="All 5 applicants will be allocated here"
            />
          </Stack>
        </UIKitSection>

        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <UIKitSection title="Forms" subtitle="Formik-ready field wrappers">
              <Formik
                initialValues={formInitialValues}
                validationSchema={formValidationSchema}
                onSubmit={(values, { setSubmitting }) => {
                  console.log("UIKit form values", values);
                  setSubmitting(false);
                }}
              >
                {({ isSubmitting, resetForm }) => (
                  <Form>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <UIInput
                          label="Field Input"
                          name="companyName"
                          placeholder="Al-Riyadh Events Co."
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <UISearchField
                          label="Search Input"
                          name="search"
                          placeholder="Search companies"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <UIPasswordField
                          label="Password Input"
                          name="password"
                          placeholder="Enter password"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <UISelect
                          label="Select"
                          name="status"
                          options={selectOptions}
                          placeholder="Select status"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <UIPhoneField
                          label="Phone Number"
                          name="phone"
                          placeholder="50 123 4567"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <UIDatePicker
                          label="Date Picker"
                          name="launchDate"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <UIDateRangeFields
                          startLabel="Start Date"
                          endLabel="End Date"
                          startDate={rangeDemoStart}
                          endDate={rangeDemoEnd}
                          onStartDateChange={setRangeDemoStart}
                          onEndDateChange={setRangeDemoEnd}
                          rangeErrorMessage="End date can't be before start date"
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <UITimeField
                          label="Time"
                          name="shiftTime"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <UINumberField
                          label="Input Number"
                          name="budget"
                          placeholder="0.00"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <UIRadioGroup
                          label="Radio"
                          name="roleType"
                          options={roleOptions}
                          row
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <UICheckbox
                          name="termsAccepted"
                          label="Checkbox - I agree to the policy"
                          helperText="Toggle and submit to test touched/error behavior."
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <UIToggle
                          name="autoApprove"
                          label="Toggle - Auto approve eligible requests"
                          helperText="Formik controlled boolean field."
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <UITextarea
                          label="Textarea"
                          name="notes"
                          placeholder="Describe the role requirements and shift notes..."
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <UITextField
                          label="Disabled Field"
                          name="disabledField"
                          value="Cannot be edited"
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <UITextField
                          label="Manual Error Example"
                          name="manualErrorField"
                          value="Invalid value"
                          error
                          helperText="Use numbers only."
                          disabled
                        />
                      </Grid>
                    </Grid>

                    <Stack
                      className="ui-kit-form-actions"
                      direction="row"
                      spacing={2}
                      justifyContent="flex-end"
                    >
                      <UIButton
                        variant="outlined"
                        type="button"
                        onClick={() => resetForm()}
                      >
                        Reset
                      </UIButton>
                      <UIButton
                        appearance="gradient"
                        type="submit"
                        loading={isSubmitting}
                      >
                        Validate Form
                      </UIButton>
                    </Stack>
                  </Form>
                )}
              </Formik>
            </UIKitSection>
          </Grid>
          <Grid item xs={12} md={5}>
            <UIKitSection
              title="Modal"
              subtitle="Typed dialog wrapper"
            >
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                {["info", "success", "destructive"].map((type) => (
                  <UIButton
                    key={type}
                    variant={type === "info" ? "primary" : type}
                    onClick={() => {
                      setModalType(type);
                      setModalOpen(true);
                    }}
                  >
                    {type}
                  </UIButton>
                ))}
              </Stack>
            </UIKitSection>
          </Grid>
          <Grid item xs={12} md={5}>
            <UIKitSection
              title="File Preview Modal"
              subtitle="Inline image / PDF preview with Download and Close"
            >
              <UIButton onClick={() => setPreviewOpen(true)}>
                Open preview
              </UIButton>
            </UIKitSection>
          </Grid>
          <Grid item xs={12} md={7}>
            <UIKitSection
              title="Action Modal"
              subtitle="Reusable approval/rejection header and user summary"
            >
              <Stack spacing={2}>
                <UIActionModal.Icon variant="approve" />
                <UIUserInfo
                  name="Lina Omar Al-Qahtani"
                  description="Requested Role: Coordinator"
                />
                <UIActionModal.Divider />
                <Typography className="ui-action-modal__body-text">
                  This action requires your attention. Please review the details
                  before proceeding.
                </Typography>
                <UIChangeSummary
                  items={["Coordinator", "Usher", "--Full Time Shift--"]}
                  description="Applicants will be notified of the role & shift changes."
                />
                <UIChangeSummary
                  tone="outline"
                  separator="swap"
                  label="Replacing"
                  items={["Fasisal Al-Qtab", "Lina Omar Al-Qahtani"]}
                />
                <UIActionModal.Actions
                  cancelLabel="Cancel"
                  confirmLabel="Approve"
                  onCancel={() => {}}
                  onConfirm={() => {}}
                />
              </Stack>
            </UIKitSection>
          </Grid>
        </Grid>

        <UIKitSection title="Tabs">
          <UITabs
            value={tab}
            onChange={setTab}
            tabs={[
              { label: "Overview", value: "overview" },
              { label: "Financials", value: "financials" },
              { label: "Relationship", value: "relationship" },
            ]}
          />
          <Divider sx={{ my: 2 }} />
          <Typography color="text.secondary">
            Selected tab: <strong>{tab}</strong>
          </Typography>
        </UIKitSection>

        <UIKitSection title="Tab Sets Levels" subtitle="Compact request status tabs">
          <TabSetsLevels
            value={statusTab}
            onChange={setStatusTab}
            tabs={[
              {
                label: (
                  <span className="event-applicants__tab-label">
                    Pending <span className="event-applicants__count">12</span>
                  </span>
                ),
                value: "pending",
              },
              {
                label: "Joined",
                value: "joined",
                icon: <UsersRound />,
              },
              { label: "Rejected", value: "rejected" },
            ]}
          />
        </UIKitSection>

        <UIKitSection title="Bulk Actions" subtitle="Selection actions for list pages">
          <UIBulkActions
            selectedCount={5}
            selectedLabel="5 Selected"
            actions={[
              { key: "approve", label: "Approve", tone: "success" },
              { key: "reject", label: "Reject", tone: "danger" },
            ]}
            menuItems={[
              { key: "addEditNote", label: "Add/Edit note" },
              { key: "deleteNote", label: "Delete Note", tone: "danger" },
            ]}
          />
        </UIKitSection>

        <UIKitSection title="Empty State" subtitle="Reusable empty content layout">
          <UIEmptyState
            title="No State"
            description="All applicant requests have been reviewed. New requests will appear here."
            icon={<UserRoundX />}
            actionLabel="CTA"
            actionIcon={<AddIcon fontSize="small" />}
            onAction={() => {}}
          />
        </UIKitSection>

        <UIKitSection
          title="Progress Bar"
          subtitle="Determinate bar — optional dot, generic tone colours"
        >
          <Stack spacing={2.5} sx={{ maxWidth: 320 }}>
            <UIProgressBar value={72} />
            <UIProgressBar value={72} showDot />
            <UIProgressBar value={88} color="success" showDot />
            <UIProgressBar value={58} color="warning" showDot />
            <UIProgressBar value={39} color="error" showDot />
            <UIProgressBar
              value={62}
              color="success"
              trackColor="var(--color-primary-100)"
            />
            <UIProgressBar value={45} size="sm" />
            <UIProgressBar value={45} size="xs" />
          </Stack>
        </UIKitSection>

        <UIKitSection title="Accordion" subtitle="Collapsible card surface">
          <UIAccordion
            id="ui-kit-zone-accordion"
            defaultExpanded
            title="Zone 01"
            action="Remove Zone"
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <UITextField
                  label="Zone Name"
                  value="Main Entrance"
                  disabled
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <UITextField
                  label="Supervisor Name"
                  value="Sara Ahmed"
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} md={4}>
                <UITextField
                  label="Zone Manager"
                  value="Raed Ahmed Subhi"
                  disabled
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <UITextField
                  label="Manager Email"
                  value="raed@proline.sa"
                  disabled
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <UITextField
                  label="Manager Phone"
                  value="+966 560 090 603"
                  disabled
                  required
                />
              </Grid>
            </Grid>
          </UIAccordion>
        </UIKitSection>

        <UIKitSection title="Search & Filter" subtitle="Shared list search, filters, and select controls">
          <Stack spacing={3}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <UISearchField
                name="standaloneSearch"
                placeholder="Search Companies"
                value={searchFilterQuery}
                onValueChange={setSearchFilterQuery}
              />
              <UIButton
                variant="primary"
                appearance="outline"
                size="large"
                sx={{ width: 110 }}
                startIcon={<ListFilter size={16} aria-hidden="true" />}
              >
                Filter
              </UIButton>
            </Box>
            <Grid container spacing={2}>
              {["status", "role", "city", "company"].map((name) => (
                <Grid item xs={12} md={3} key={name}>
                  <UISelect
                    name={`searchFilter.${name}`}
                    placeholder="Placeholder text"
                    options={selectOptions}
                    value={searchFilterValues[name]}
                    onChange={updateSearchFilter(name)}
                  />
                </Grid>
              ))}
            </Grid>
            <Filters
              searchInput={{
                name: "sharedFilterSearch",
                placeholder: "Search Companies",
                value: searchFilterQuery,
                onChange: setSearchFilterQuery,
              }}
              filters={[
                {
                  type: "filter",
                  name: "status",
                  placeholder: "Placeholder text",
                  options: selectOptions,
                  value: searchFilterValues.status,
                  onChange: updateSearchFilter("status"),
                  showLabel: false,
                },
                {
                  type: "filter",
                  name: "role",
                  placeholder: "Placeholder text",
                  options: selectOptions,
                  value: searchFilterValues.role,
                  onChange: updateSearchFilter("role"),
                  showLabel: false,
                },
                {
                  type: "filter",
                  name: "city",
                  placeholder: "Placeholder text",
                  options: selectOptions,
                  value: searchFilterValues.city,
                  onChange: updateSearchFilter("city"),
                  showLabel: false,
                },
                {
                  type: "filter",
                  name: "company",
                  placeholder: "Placeholder text",
                  options: selectOptions,
                  value: searchFilterValues.company,
                  onChange: updateSearchFilter("company"),
                  showLabel: false,
                },
              ]}
              defaultExpanded
            />
          </Stack>
        </UIKitSection>

        <UIKitSection title="Toggle" subtitle="Compact boolean control states">
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <UIToggle
              checked={notificationsEnabled}
              onChange={setNotificationsEnabled}
              label="Notifications"
            />
            <UIToggle checked label="Enabled" onChange={() => {}} />
            <UIToggle checked={false} label="Disabled off" disabled onChange={() => {}} />
            <UIToggle checked label="Disabled on" disabled onChange={() => {}} />
          </Stack>
        </UIKitSection>

        <UIKitSection title="Table" subtitle="Initial DataGrid wrapper">
          <UITable rows={tableRows} columns={tableColumns} height={330} />
        </UIKitSection>

        <UIKitSection title="Table with checkbox selection" subtitle="Initial DataGrid wrapper">
          <UITable rows={tableRows} columns={tableColumns} height={330} checkboxSelection />
        </UIKitSection>

        <UIKitSection title="Pagination bar" subtitle="Range text, page control, rows-per-page select">
          <UIPaginationBar
            currentPage={paginationPage}
            pages={4}
            pageSize={paginationPageSize}
            rangeTemplate="Showing {from}–{to} of {total} items"
            rangeFrom={(paginationPage - 1) * paginationPageSize + 1}
            rangeTo={Math.min(paginationPage * paginationPageSize, 36)}
            rangeTotal={36}
            rowsPerPageLabel="Rows per page"
            onPageChange={setPaginationPage}
            onPageSizeChange={setPaginationPageSize}
          />
        </UIKitSection>
      </Stack>

      <UIModal
        open={modalOpen}
        type={modalType}
        title="Modal heading"
        description="This action requires your attention. Please review the details before proceeding."
        onClose={() => setModalOpen(false)}
        onCancel={() => setModalOpen(false)}
        onConfirm={() => setModalOpen(false)}
        cancelLabel="Cancel"
        confirmLabel="Confirm"
      />

      <FilePreviewModal
        open={previewOpen}
        fileName="proof-of-transaction.png"
        contentType="image/png"
        meta="Uploaded by Lina Omar • 24-08-2026 10:15"
        fileUrl={
          "data:image/svg+xml;charset=utf-8," +
          encodeURIComponent(
            '<svg xmlns="http://www.w3.org/2000/svg" width="600" height="400"><rect width="600" height="400" fill="#eef1f6"/><text x="300" y="205" font-family="sans-serif" font-size="28" fill="#6b7280" text-anchor="middle">Sample preview</text></svg>',
          )
        }
        labels={{
          download: "Download",
          close: "Close",
          unsupported:
            "Preview is not available for this file type. Download the file to view it.",
          loadError: "Unable to load preview",
          pageOf: "Page {page} of {pages}",
          previousPage: "Previous page",
          nextPage: "Next page",
        }}
        onDownload={() => setPreviewOpen(false)}
        onClose={() => setPreviewOpen(false)}
      />
    </main>
  );
};

export default UIKit;
